import { useEffect, useState, useCallback, useRef } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { MessageSquare, Mail, CheckCheck, Bell, AlertTriangle, Link2 } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import EmailViewerDialog from "@/components/EmailViewerDialog";

function formatWhen(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const now = new Date();
    const sameDay = d.toDateString() === now.toDateString();
    return sameDay
      ? d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
      : d.toLocaleDateString([], { month: "short", day: "numeric" });
  } catch {
    return "";
  }
}

export default function UnreadCommsCard() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [openEmailId, setOpenEmailId] = useState(null);
  const nav = useNavigate();
  const loc = useLocation();
  const cardRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/comms/unread?limit=25");
      setItems(data?.items || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  // Sidebar badge taps add `#unread` to the URL; scroll the card into view.
  useEffect(() => {
    if (loc.hash === "#unread" && cardRef.current) {
      cardRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      // Briefly highlight so the user sees where attention is.
      cardRef.current.classList.add("ring-2", "ring-[#FF5A00]", "ring-offset-2");
      const t = setTimeout(() => {
        cardRef.current?.classList.remove("ring-2", "ring-[#FF5A00]", "ring-offset-2");
      }, 1600);
      return () => clearTimeout(t);
    }
  }, [loc.hash, loading, items.length]);

  const openItem = async (it) => {
    // Emails open in the full viewer modal (which marks read on view).
    if (it.channel === "email") {
      setOpenEmailId(it.id);
      return;
    }
    try {
      if (it.customer_id) {
        await api.post("/comms/mark-read", { customer_id: it.customer_id });
      } else if (it.lead_id) {
        await api.post("/comms/mark-read", { lead_id: it.lead_id });
      } else {
        await api.post("/comms/mark-read", { ids: [it.id] });
      }
    } catch { /* non-fatal */ }
    if (it.customer_id) nav(`/app/customers/${it.customer_id}`);
    else if (it.lead_id) nav(`/app/leads/${it.lead_id}`);
    await load();
  };

  const markAll = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/comms/mark-all-read");
      toast.success(`Marked ${data?.updated || 0} as read`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Mark all failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      ref={cardRef}
      id="unread"
      className="bg-white border border-zinc-200 rounded-xl card-crisp p-6 mb-8 transition-shadow"
      data-testid="unread-comms-card"
    >
      <div className="flex items-center gap-3 mb-4">
        <div className="w-9 h-9 rounded-lg bg-orange-50 flex items-center justify-center">
          <Bell className="w-4 h-4 text-[#FF5A00]" />
        </div>
        <div className="flex-1">
          <div className="text-xs font-mono-accent uppercase tracking-wider text-zinc-500">
            Inbox
          </div>
          <div className="font-heading text-lg font-bold" data-testid="unread-comms-title">
            Unread communications
            {items.length > 0 && (
              <span
                className="ml-2 inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded-full bg-[#FF5A00] text-white text-xs font-mono-accent"
                data-testid="unread-comms-count-pill"
              >
                {items.length}
              </span>
            )}
          </div>
        </div>
        {items.length > 0 && (
          <Button
            variant="outline"
            size="sm"
            onClick={markAll}
            disabled={busy}
            data-testid="unread-comms-mark-all-btn"
          >
            <CheckCheck className="w-4 h-4 mr-1" />
            Mark all read
          </Button>
        )}
      </div>

      {loading ? (
        <div className="text-sm text-zinc-500" data-testid="unread-comms-loading">
          Loading…
        </div>
      ) : items.length === 0 ? (
        <div
          className="text-sm text-zinc-500 py-4 text-center bg-zinc-50 rounded-lg"
          data-testid="unread-comms-empty"
        >
          You're all caught up — no new customer replies.
        </div>
      ) : (
        <ul className="divide-y divide-zinc-100" data-testid="unread-comms-list">
          {items.map((it) => {
            const isFailure = it.kind === "failure";
            const isUnmatched = it.kind === "unmatched";
            const Icon = isFailure ? AlertTriangle
                            : isUnmatched ? Link2
                            : (it.channel === "email" ? Mail : MessageSquare);
            const iconBg = isFailure ? "bg-red-50"
                            : isUnmatched ? "bg-amber-50"
                            : "bg-orange-50";
            const iconColor = isFailure ? "text-red-600"
                               : isUnmatched ? "text-amber-700"
                               : "text-[#FF5A00]";
            return (
              <li key={it.id}>
                <button
                  type="button"
                  onClick={() => openItem(it)}
                  className="w-full flex items-start gap-3 py-3 text-left hover:bg-zinc-50 px-2 -mx-2 rounded-md transition-colors"
                  data-testid={`unread-comms-item-${it.id}`}
                >
                  <div className={`mt-0.5 w-7 h-7 rounded-md ${iconBg} flex items-center justify-center shrink-0`}>
                    <Icon className={`w-3.5 h-3.5 ${iconColor}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2">
                      <span className="font-medium text-sm text-zinc-900 truncate">
                        {it.customer_name || it.from_number || "Unknown"}
                      </span>
                      {it.channel === "email" && it.subject && (
                        <span className="text-xs text-zinc-500 truncate">· {it.subject}</span>
                      )}
                      {isFailure && (
                        <span className="text-[9px] font-mono-accent uppercase tracking-widest text-red-700 bg-red-100 px-1.5 py-0.5 rounded" data-testid={`unread-comms-failure-badge-${it.id}`}>
                          Delivery failed
                        </span>
                      )}
                      {isUnmatched && (
                        <span className="text-[9px] font-mono-accent uppercase tracking-widest text-amber-800 bg-amber-100 px-1.5 py-0.5 rounded" data-testid={`unread-comms-unmatched-badge-${it.id}`}>
                          Unmatched
                        </span>
                      )}
                      <span className="text-[10px] font-mono-accent uppercase tracking-wider text-zinc-400 ml-auto">
                        {formatWhen(it.created_at)}
                      </span>
                    </div>
                    <div className={`text-sm truncate ${isFailure ? "text-red-700" : "text-zinc-600"}`}>{it.body || "(no message body)"}</div>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}
      <EmailViewerDialog
        emailId={openEmailId}
        open={!!openEmailId}
        onClose={() => setOpenEmailId(null)}
        onChanged={load}
      />
    </div>
  );
}
